import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const CHAPTERS = [
  {
    num: "01",
    title: "A simple process",
    body: "Tell us about your property and your situation. There is no obligation to move forward — ever. We listen first.",
    img: "https://images.unsplash.com/photo-1711048090278-30fef40e104d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    num: "02",
    title: "No traditional listing",
    body: "Explore alternatives to the conventional home-selling process — on your terms, at your pace, with none of the usual pressure.",
    img: "https://images.unsplash.com/photo-1649160697540-919919405e76?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    num: "03",
    title: "A local focus",
    body: "We work exclusively with homeowners in Columbus, Georgia and the surrounding communities. Real neighbors, real answers.",
    img: "https://images.unsplash.com/photo-1590415041359-c6dd3f511020?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
];

const Chapter = ({ chapter, flip }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className={`grid md:grid-cols-2 gap-8 md:gap-16 items-center ${flip ? "md:[direction:rtl]" : ""}`}
      data-testid={`chapter-${chapter.num}`}
    >
      <div className="md:[direction:ltr]">
        <span className="font-mono text-sm tracking-[0.3em] text-gold">{chapter.num} / 03</span>
        <h3 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-ink mt-5 leading-tight">
          {chapter.title}
        </h3>
        <p className="text-ink-soft text-base md:text-lg mt-5 max-w-md leading-relaxed">{chapter.body}</p>
      </div>
      <div className="md:[direction:ltr] relative h-[280px] md:h-[400px] clip-frame overflow-hidden shadow-xl ring-1 ring-ink/5">
        <img src={chapter.img} alt={chapter.title} className="w-full h-full object-cover" />
      </div>
    </motion.div>
  );
};

export const Manifesto = () => {
  return (
    <section id="explore" className="max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="manifesto">
      <div className="mb-16 md:mb-24 max-w-2xl">
        <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold">Explore your options</span>
        <h2 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-ink mt-6 leading-[0.95]">
          Selling, <span className="italic text-gold">reimagined</span>
        </h2>
      </div>
      <div className="space-y-20 md:space-y-32">
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.num} chapter={c} flip={i % 2 === 1} />
        ))}
      </div>
    </section>
  );
};
